import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { pushLayer } from '../state/router.ts';
import { cx } from './kit.tsx';

let openSheets = 0;

/**
 * Лист поверх экрана: снизу на телефоне, окном по центру на широком экране. Закрывается
 * тапом по затемнению, клавишей Escape и кнопкой «назад» браузера.
 *
 * `onClose` зовётся после анимации ухода. Содержимое получает `close`, чтобы закрыть лист
 * своей кнопкой с той же анимацией.
 */
export function Sheet(props: {
  onClose: () => void;
  /** Высота: `auto` по содержимому, `medium` около двух третей, `large` почти весь экран. */
  size?: 'auto' | 'medium' | 'large';
  /** На широком экране лист шире обычной формы: корзина и оформление. */
  page?: boolean;
  grabber?: boolean;
  /** Закрытие тапом по фону и «назад» выключено: идёт оплата. */
  locked?: boolean;
  label?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const { onClose, locked } = props;
  const [closing, setClosing] = useState(false);
  const release = useRef<(() => void) | null>(null);
  const done = useRef(false);

  const finish = useCallback(() => {
    if (done.current) return;
    done.current = true;
    onClose();
  }, [onClose]);

  /** Уход с анимацией; запись истории снимается, если лист закрыли не кнопкой «назад». */
  const close = useCallback(
    (fromHistory = false) => {
      if (done.current) return;
      if (!fromHistory) release.current?.();
      release.current = null;
      setClosing(true);
      setTimeout(finish, 200);
    },
    [finish],
  );

  useEffect(() => {
    release.current = pushLayer(() => close(true));
    return () => {
      // Лист убрали из дерева снаружи: его запись истории уходит вместе с ним.
      release.current?.();
      release.current = null;
    };
    // Слой истории ставится один раз на жизнь листа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Пока лист открыт, приложение под ним не ловит фокус и не читается скринридером.
  useEffect(() => {
    const root = document.getElementById('root');
    openSheets += 1;
    if (root) root.inert = true;
    return () => {
      openSheets -= 1;
      if (root && openSheets === 0) root.inert = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !locked) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, locked]);

  return createPortal(
    <div className={cx('sheet-layer', closing && 'is-closing')}>
      <div className="sheet-scrim" onClick={() => !locked && close()} />
      <div
        className={cx('sheet', props.size === 'large' && 'is-large', props.size === 'medium' && 'is-medium', props.page && 'is-page', closing && 'is-closing')}
        role="dialog"
        aria-modal="true"
        aria-label={props.label}
      >
        {props.grabber !== false && <div className="sheet-grabber" />}
        {typeof props.children === 'function' ? props.children(() => close()) : props.children}
      </div>
    </div>,
    document.body,
  );
}
